import { useTranslation } from 'react-i18next';
import BulletinSection from '@/components/common/BulletinSection';
import IdText from '@/components/common/IdText';
import TermList, { type TermItem } from '@/components/common/TermList';
import { decodeEntities, formatMoney, formatOrgName } from '@/lib/format';
import type { PurchasePriceList } from '@/types/buy';
import classes from './OfferTerms.module.css';

// Метки зачёта качества (enum бэкенда). Неизвестное значение показываем как есть (не падаем).
const CREDIT_TYPE_KEYS: Readonly<Record<string, string>> = {
  BY_AVERAGE_VALUE: 'app.buy.terms.creditAverage',
  BY_EACH_DAY: 'app.buy.terms.creditEachDay',
};

// «Условия предложения» прайс-листа («Бюллетень»): строки «подпись — значение» без рамки и значков, как условия
// предложения продавца. Порядок строк зеркалит форму: зачёт качества → приёмка → грузополучатель → прочие условия →
// комиссия. Данные приходят в прайс-листе, дополнительных запросов нет; пустые строки не выводятся.
export default function OfferTerms({ list }: { readonly list: PurchasePriceList }) {
  const { t } = useTranslation();
  const creditKey = list.qualityCreditType ? CREDIT_TYPE_KEYS[list.qualityCreditType] : undefined;
  const acceptance = list.qualityAcceptanceProperties ?? [];

  const rows: TermItem[] = [
    ...(list.qualityCreditType
      ? [{ key: 'credit', label: t('app.buy.terms.credit'), value: creditKey ? t(creditKey) : list.qualityCreditType }]
      : []),
    {
      key: 'acceptance',
      label: t('app.buy.terms.acceptance'),
      value: list.qualityAcceptanceByGost ? (
        <>
          <div>{t('app.buy.terms.acceptanceGost')}</div>
          {acceptance.length > 0 ? (
            <div>
              {acceptance
                .map((property) => `${property.name}${property.gostValue ? ` ${decodeEntities(property.gostValue)}` : ''}`)
                .join(' · ')}
            </div>
          ) : null}
        </>
      ) : (
        // Не по ГОСТу: согласованные правила «Если X превышает N %, снижение веса на M %».
        acceptance.map((property) => (
          <div key={property.name}>
            {t('app.buy.terms.qaRule', {
              name: property.name,
              limit: property.increaseValue,
              decrease: property.decreaseWeightValue,
            })}
          </div>
        ))
      ),
    },
    ...(list.consigneeName
      ? [
          {
            key: 'consignee',
            label: t('app.buy.form.consignee'),
            value: (
              <>
                <div>{formatOrgName(list.consigneeName)}</div>
                {list.consigneeInn || list.consigneeKpp ? (
                  <div className={classes.ids}>
                    {list.consigneeInn ? (
                      <>
                        {t('auth.fields.inn')} <IdText>{list.consigneeInn}</IdText>
                      </>
                    ) : null}
                    {list.consigneeInn && list.consigneeKpp ? ' · ' : null}
                    {list.consigneeKpp ? (
                      <>
                        {t('app.company.card.kpp')} <IdText>{list.consigneeKpp}</IdText>
                      </>
                    ) : null}
                  </div>
                ) : null}
              </>
            ),
          },
        ]
      : []),
    ...(list.userComment
      ? [{ key: 'comment', label: t('app.buy.terms.comment'), value: <span className={classes.comment}>{list.userComment}</span> }]
      : []),
    ...(list.reward
      ? [{ key: 'reward', label: t('app.buy.terms.commission'), value: t('app.buy.terms.commissionValue', { value: formatMoney(list.reward) }) }]
      : []),
  ];

  return (
    <BulletinSection title={t('app.buy.terms.title')}>
      <TermList items={rows} />
    </BulletinSection>
  );
}
